import React, { useCallback, useEffect, useState } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

/**
 * Every confirmed Stock Report for a branch. The files are never changed after
 * upload; opening an old one shows its original quantities with today's
 * supplier mapping.
 */
const money = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StockUploadsTab = ({ businessUnits, branchId, onBranchChange, isAdmin, onView, onToast }) => {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState('');

    const load = useCallback(async () => {
        if (!branchId) return;
        setLoading(true);
        setError('');
        try {
            const data = await stockReorderApi.listStockUploads(branchId);
            setUploads(data.uploads || []);
        } catch (err) {
            setError(errorMessage(err, 'Could not load stock uploads'));
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (u) => {
        if (
            !window.confirm(
                `Delete the stock report as on ${u.as_on_date}?${
                    u.status === 'active' ? '\n\nIt is the current stock — the newest older report becomes current.' : ''
                }\n\nThis cannot be undone.`
            )
        )
            return;
        setBusyId(u.id);
        setError('');
        try {
            await stockReorderApi.deleteStockUpload(u.id);
            onToast?.('Stock report deleted.');
            await load();
        } catch (err) {
            setError(errorMessage(err, 'Could not delete that report'));
        } finally {
            setBusyId('');
        }
    };

    return (
        <section className="sr-card">
            <div className="sr-stock-toolbar">
                <div className="sr-field sr-stock-branch">
                    <label htmlFor="sr-uploads-branch">Branch</label>
                    <select id="sr-uploads-branch" value={branchId} onChange={(e) => onBranchChange(e.target.value)}>
                        {businessUnits.map((bu) => (
                            <option key={bu.id} value={bu.id}>
                                {bu.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className="sr-alert sr-alert-error">{error}</div>}
            {loading && <div className="sr-empty">Loading…</div>}
            {!loading && !error && uploads.length === 0 && (
                <div className="sr-empty">No stock reports uploaded for this branch yet.</div>
            )}

            {!loading && uploads.length > 0 && (
                <div className="sr-table-wrap">
                    <table className="sr-table sr-uploads-table">
                        <thead>
                            <tr>
                                <th>As on</th>
                                <th>File</th>
                                <th>Uploaded by</th>
                                <th className="sr-right">Items</th>
                                <th className="sr-right">Value</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {uploads.map((u) => (
                                <tr key={u.id}>
                                    <td>{u.as_on_date}</td>
                                    <td className="sr-wrap">{u.filename}</td>
                                    <td className="sr-wrap">
                                        {u.uploaded_by}
                                        <div className="sr-muted">
                                            {u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : ''}
                                        </div>
                                    </td>
                                    <td className="sr-right">{u.item_count.toLocaleString()}</td>
                                    <td className="sr-right">{money(u.total_value)}</td>
                                    <td>
                                        {u.status === 'active' ? (
                                            <span className="sr-tag sr-tag-ok">Current</span>
                                        ) : (
                                            <span className="sr-tag">History</span>
                                        )}
                                    </td>
                                    <td className="sr-right">
                                        <button className="sr-btn sr-btn-sm" onClick={() => onView(u)}>
                                            View
                                        </button>
                                        {isAdmin && (
                                            <button
                                                className="sr-btn sr-btn-sm sr-btn-danger"
                                                onClick={() => remove(u)}
                                                disabled={busyId === u.id}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default StockUploadsTab;
